import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import './Hero.css';

/* ─── 3-D SCENE ─────────────────────────────────────────────── */
function initThreeScene(canvas) {
  const W = canvas.parentElement.clientWidth;
  const H = canvas.parentElement.clientHeight;

  /* Renderer */
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(W, H);
  renderer.setClearColor(0x000000, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  /* Scene & Camera */
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(52, W / H, 0.1, 100);
  camera.position.set(0, 0.2, 6);

  /* ── Lights ── */
  scene.add(new THREE.AmbientLight(0xffffff, 0.55));

  const sun = new THREE.DirectionalLight(0xffffff, 0.9);
  sun.position.set(5, 7, 5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  scene.add(sun);

  const fill = new THREE.PointLight(0x4488ff, 1.4, 18);
  fill.position.set(-4, 2, 3);
  scene.add(fill);

  const warm = new THREE.PointLight(0xff8833, 0.9, 14);
  warm.position.set(4, -2, 4);
  scene.add(warm);

  /* ── Book Group ── */
  const book = new THREE.Group();
  scene.add(book);

  const spineMat = new THREE.MeshStandardMaterial({ color: 0x0d2f6e, roughness: 0.35, metalness: 0.3 });
  book.add(Object.assign(new THREE.Mesh(new THREE.BoxGeometry(0.2, 2.3, 0.6), spineMat)));

  const coverMat = new THREE.MeshStandardMaterial({ color: 0x1a56db, roughness: 0.28, metalness: 0.22 });
  const lCover = new THREE.Mesh(new THREE.BoxGeometry(1.35, 2.3, 0.07), coverMat);
  lCover.position.set(-0.76, 0, 0.27);
  lCover.rotation.y = 0.19;
  book.add(lCover);

  const rCover = new THREE.Mesh(new THREE.BoxGeometry(1.35, 2.3, 0.07), new THREE.MeshStandardMaterial({ color: 0x1a56db, roughness: 0.28, metalness: 0.22 }));
  rCover.position.set(0.76, 0, 0.27);
  rCover.rotation.y = -0.19;
  book.add(rCover);

  /* Green accent strip */
  const greenMat = new THREE.MeshStandardMaterial({ color: 0x1e8a45, roughness: 0.4, metalness: 0.18 });
  [-0.76, 0.76].forEach((x, i) => {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.32, 0.08), greenMat);
    strip.position.set(x, -0.82, 0.27);
    strip.rotation.y = i === 0 ? 0.19 : -0.19;
    book.add(strip);
  });

  /* Pages */
  const pageMat = new THREE.MeshStandardMaterial({ color: 0xfafaf5, roughness: 0.88 });
  for (let i = 0; i < 14; i++) {
    const pg = new THREE.Mesh(new THREE.BoxGeometry(1.3, 2.22, 0.013), pageMat);
    pg.position.set(0, 0, 0.26 - i * 0.007);
    book.add(pg);
  }

  /* Line decorations on cover */
  const lineMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.28 });
  for (let j = 0; j < 5; j++) {
    const ln = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.014, 0.08), lineMat);
    ln.position.set(0.76, 0.62 - j * 0.3, 0.31);
    ln.rotation.y = -0.19;
    book.add(ln);
  }

  book.position.set(0, -0.25, 0);
  book.rotation.x = 0.1;

  /* ── Floating Stars (Octahedra) ── */
  const starDefs = [
    { color: 0xff6f00, size: 0.20 },
    { color: 0xe53935, size: 0.17 },
    { color: 0x2e7d32, size: 0.19 },
    { color: 0x1565c0, size: 0.16 },
    { color: 0xf5a623, size: 0.14 },
  ];
  const stars = starDefs.map((d, i) => {
    const mat = new THREE.MeshStandardMaterial({ color: d.color, roughness: 0.14, metalness: 0.72 });
    const m = new THREE.Mesh(new THREE.OctahedronGeometry(d.size, 0), mat);
    m.castShadow = true;
    m._speed  = 0.28 + i * 0.07;
    m._radius = 2.3 + (i % 3) * 0.25;
    m._phase  = (i / starDefs.length) * Math.PI * 2;
    m._baseY  = -0.6 + i * 0.4;
    scene.add(m);
    return m;
  });

  /* ── Orbiting Spheres ── */
  const orbitDefs = [
    { color: 0xfbbf24, r: 2.9, spd: 0.45, ph: 0,       y: -0.3  },
    { color: 0x4ade80, r: 3.2, spd: 0.35, ph: Math.PI,  y:  0.2  },
    { color: 0xf87171, r: 3.0, spd: 0.55, ph: Math.PI/2,y: -0.55 },
    { color: 0x60a5fa, r: 3.4, spd: 0.30, ph: 3*Math.PI/2, y: 0.45 },
  ];
  const orbs = orbitDefs.map(d => {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(0.085, 14, 14),
      new THREE.MeshStandardMaterial({ color: d.color, roughness: 0.18, metalness: 0.55 })
    );
    Object.assign(m, { _r: d.r * 0.7, _spd: d.spd, _ph: d.ph, _baseY: d.y });
    scene.add(m);
    return m;
  });

  /* ── Particles ── */
  const ptGeo = new THREE.BufferGeometry();
  const ptCount = 160;
  const ptPos = new Float32Array(ptCount * 3);
  for (let i = 0; i < ptCount * 3; i++) ptPos[i] = (Math.random() - 0.5) * 16;
  ptGeo.setAttribute('position', new THREE.BufferAttribute(ptPos, 3));
  const pts = new THREE.Points(ptGeo, new THREE.PointsMaterial({ color: 0x6699ff, size: 0.055, transparent: true, opacity: 0.65 }));
  scene.add(pts);

  /* ── Mouse parallax ── */
  const mouse = { x: 0, y: 0 };
  const onMouse = (e) => {
    mouse.x = (e.clientX / window.innerWidth  - 0.5) * 2;
    mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2;
  };
  window.addEventListener('mousemove', onMouse);

  /* ── Animate ── */
  const clock = new THREE.Clock();
  let raf;

  function animate() {
    raf = requestAnimationFrame(animate);
    const t = clock.getElapsedTime();

    book.rotation.y = Math.sin(t * 0.38) * 0.28 + mouse.x * 0.10;
    book.rotation.x = 0.10 + Math.sin(t * 0.28) * 0.07 + mouse.y * 0.05;
    book.position.y = -0.25 + Math.sin(t * 0.55) * 0.09;

    stars.forEach((s, i) => {
      const a = t * s._speed + s._phase;
      s.position.set(Math.cos(a) * s._radius, s._baseY + Math.sin(t * 0.5 + i) * 0.28, Math.sin(a) * s._radius * 0.45 + 0.8);
      s.rotation.x += 0.020; s.rotation.y += 0.032;
    });

    orbs.forEach((o, i) => {
      const a = t * o._spd + o._ph;
      o.position.set(Math.cos(a) * o._r, o._baseY + Math.sin(t * 0.38 + i) * 0.14, Math.sin(a) * o._r * 0.4 + 0.5);
      o.rotation.x += 0.04; o.rotation.y += 0.04;
    });

    pts.rotation.y = t * 0.018;
    pts.rotation.x = t * 0.009;
    fill.position.set(Math.sin(t * 0.4) * 4, Math.cos(t * 0.28) * 3, 3);

    renderer.render(scene, camera);
  }
  animate();

  /* ── Resize ── */
  const onResize = () => {
    if (!canvas.parentElement) return;
    const nW = canvas.parentElement.clientWidth;
    const nH = canvas.parentElement.clientHeight;
    camera.aspect = nW / nH;
    camera.updateProjectionMatrix();
    renderer.setSize(nW, nH);
  };
  window.addEventListener('resize', onResize);

  /* ── Cleanup ── */
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('mousemove', onMouse);
    window.removeEventListener('resize', onResize);
    renderer.dispose();
  };
}

/* ─── COMPONENT ─────────────────────────────────────────────── */
export default function Hero() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const cleanup = initThreeScene(canvasRef.current);
    return cleanup;
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="hero" id="home">
      {/* Text Column */}
      <div className="hero-text">
        <div className="hero-badge">
          <span className="badge-dot" />
          Empowering Every Learner
        </div>

        <h1 className="hero-title">
          Unlock Every Child's<br />
          <span className="hero-accent">True Potential</span>
        </h1>

        <p className="hero-sub">
          Personalized educational support tailored to individual learning challenges —
          from remedial teaching to counselling psychology.
        </p>

        <div className="hero-btns">
          <button className="btn btn-white" onClick={() => scrollTo('contact')}>Enquire Now</button>
          <button className="btn btn-ghost" onClick={() => scrollTo('services')}>Our Services →</button>
        </div>

        <div className="hero-stats">
          {[
            { num: '10+',   label: 'PROGRAMMES'       },
            { num: '15+',  label: 'YEARS EXPERIENCE'  },
            { num: '500+', label: 'STUDENTS HELPED'   },
          ].map((s) => (
            <div className="hero-stat" key={s.label}>
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Three.js Canvas Column */}
      <div className="hero-canvas-wrap">
        <canvas ref={canvasRef} className="three-canvas" />
      </div>
    </section>
  );
}
